import {
  HeartIcon as HeartOutlineIcon,
  TrashIcon,
} from '@heroicons/react/outline';
import { CheckIcon, HeartIcon, LockClosedIcon } from '@heroicons/react/solid';
import Link from 'next/link';
import React from 'react';
import { Voice } from 'types';

import clsxm from '@/lib/clsxm';

import { SimpleTooltip } from '@/components/ui/tooltip';

import VoiceAvatar from './VoiceAvatar';

export interface VoiceBadge {
  key: string;
  label: string;
  tone: 'primary' | 'emerald' | 'amber' | 'gray';
  icon?: React.ReactNode;
}

export interface VoiceLanguageInfo {
  flag: string;
  label: string;
  accent?: string;
  /** Further languages the voice speaks, shown as +N with a tooltip. */
  others: string[];
}

export interface VoiceRowLabels {
  play: string;
  pause: string;
  use: string;
  selected: string;
  upgrade: string;
  favorite: string;
  unfavorite: string;
  remove: string;
}

interface VoiceRowProps {
  voice: Voice;
  name: string;
  /** "Female · Young · Narrative & Story · Calm" */
  meta: string;
  language: VoiceLanguageInfo | null;
  badges: VoiceBadge[];
  selected: boolean;
  playing: boolean;
  favorite: boolean;
  /** Free plan: the voice needs an upgrade and can't be selected. */
  locked: boolean;
  deletable: boolean;
  /**
   * Keep room for the delete button / an Upgrade button on rows without
   * one, so every row's language column lines up. Set when any voice in the
   * list has one.
   */
  reserveDeleteSlot?: boolean;
  reserveUpgradeSlot?: boolean;
  labels: VoiceRowLabels;
  onSelect: () => void;
  /** Select and close the picker. */
  onUse: () => void;
  onTogglePlay?: () => void;
  onToggleFavorite: () => void;
  onDelete: () => void;
}

const BADGE_TONES: Record<VoiceBadge['tone'], string> = {
  primary:
    'bg-primary-50 text-primary-700 dark:bg-primary-500/15 dark:text-primary-300',
  emerald:
    'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300',
  amber: 'bg-amber-50 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300',
  gray: 'bg-gray-100 text-gray-600 dark:bg-white/10 dark:text-slate-300',
};

// Actions appear on hover; touch screens have no hover, so they always show.
const REVEAL =
  'opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100 [@media(hover:none)]:opacity-100';

// The row's main button (Use / Selected / Upgrade).
const PILL = 'h-8 items-center rounded-full text-xs font-semibold';
// Hidden copies of each state of that button, stacked in one grid cell.
const GHOST_PILL = 'col-start-1 row-start-1 inline-flex';

const IconButton = ({
  label,
  onClick,
  className,
  pressed,
  children,
}: {
  label: string;
  onClick: () => void;
  className?: string;
  pressed?: boolean;
  children: React.ReactNode;
}) => (
  <button
    type='button'
    aria-label={label}
    title={label}
    aria-pressed={pressed}
    onClick={(event) => {
      event.stopPropagation();
      onClick();
    }}
    className={clsxm(
      'inline-flex h-8 w-8 items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-700',
      'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:text-slate-500 dark:hover:bg-white/10 dark:hover:text-white',
      className,
    )}
  >
    {children}
  </button>
);

const VoiceRow = ({
  voice,
  name,
  meta,
  language,
  badges,
  selected,
  playing,
  favorite,
  locked,
  deletable,
  reserveDeleteSlot = false,
  reserveUpgradeSlot = false,
  labels,
  onSelect,
  onUse,
  onTogglePlay,
  onToggleFavorite,
  onDelete,
}: VoiceRowProps) => {
  const languageText = language
    ? [language.label, language.accent].filter(Boolean).join(' ')
    : '';

  return (
    <li
      onClick={locked ? undefined : onSelect}
      onDoubleClick={locked ? undefined : onUse}
      className={clsxm(
        'group relative flex items-center gap-3 rounded-xl px-2.5 py-2 transition-colors sm:px-3',
        locked ? 'cursor-default' : 'cursor-pointer',
        selected
          ? 'bg-primary-50/80 ring-1 ring-inset ring-primary-200 dark:bg-primary-500/10 dark:ring-primary-400/25'
          : 'hover:bg-gray-50 dark:hover:bg-white/[0.04]',
      )}
    >
      <VoiceAvatar
        voice={voice}
        playing={playing}
        onTogglePlay={onTogglePlay}
        playLabel={playing ? labels.pause : labels.play}
      />
      <button
        type='button'
        disabled={locked}
        aria-pressed={selected}
        onClick={(event) => {
          event.stopPropagation();
          if (!locked) onSelect();
        }}
        className='flex min-w-0 flex-1 items-center gap-4 rounded-lg text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 disabled:cursor-default'
      >
        <span className='min-w-0 flex-1' title={voice.description || undefined}>
          <span className='flex min-w-0 items-center gap-1.5'>
            <span className='truncate text-sm font-medium text-gray-900 dark:text-slate-100'>
              {name}
            </span>
            {badges.map((badge) => (
              <span
                key={badge.key}
                className={clsxm(
                  'inline-flex shrink-0 items-center gap-0.5 rounded-md px-1.5 py-0.5 text-[10.5px] font-semibold leading-none',
                  BADGE_TONES[badge.tone],
                )}
              >
                {badge.icon}
                {badge.label}
              </span>
            ))}
          </span>
          {(meta || language) && (
            <span className='mt-1 block truncate text-[12.5px] leading-tight text-gray-500 dark:text-slate-400'>
              {/* Phones have no language column: lead with the language. */}
              {language && (
                <span className='md:hidden'>
                  {language.flag ? `${language.flag} ` : ''}
                  {language.label}
                  {meta ? ' · ' : ''}
                </span>
              )}
              {meta}
            </span>
          )}
        </span>
        {language && (
          <span className='hidden w-44 shrink-0 items-center gap-1.5 text-[13px] text-gray-700 dark:text-slate-300 md:flex'>
            {language.flag && (
              <span className='text-sm leading-none'>{language.flag}</span>
            )}
            <span
              className='flex min-w-0 items-center gap-1.5'
              title={languageText}
            >
              <span className='truncate'>{language.label}</span>
              {language.accent && (
                <span className='truncate text-gray-400 dark:text-slate-500'>
                  {language.accent}
                </span>
              )}
            </span>
            {language.others.length > 0 && (
              // pointer-events-none: the tooltip is portaled outside the
              // dialog, and a click on it would count as an outside click.
              <SimpleTooltip
                className='pointer-events-none'
                title={
                  <ul className='space-y-0.5 py-0.5'>
                    {language.others.map((other) => (
                      <li key={other}>{other}</li>
                    ))}
                  </ul>
                }
              >
                <span className='shrink-0 rounded-md bg-gray-100 px-1.5 py-px text-[11px] font-medium text-gray-500 dark:bg-white/10 dark:text-slate-400'>
                  +{language.others.length}
                </span>
              </SimpleTooltip>
            )}
          </span>
        )}
      </button>
      <div className='grid shrink-0 items-center'>
        {/* A hidden copy of the widest set of buttons any row can show shares
            this grid cell, so every row keeps the same width and the language
            column lines up (from md up, where that column shows). The real
            buttons hug the right edge, so the heart stays next to them. */}
        <div
          aria-hidden='true'
          className='invisible col-start-1 row-start-1 hidden items-center gap-0.5 md:flex'
        >
          {reserveDeleteSlot && <span className='h-8 w-8' />}
          <span className='h-8 w-8' />
          <span className='ml-1 grid'>
            <span className={clsxm(PILL, GHOST_PILL, 'gap-1 pl-2 pr-3')}>
              <span className='h-4 w-4' />
              {labels.selected}
            </span>
            <span className={clsxm(PILL, GHOST_PILL, 'px-3.5')}>
              {labels.use}
            </span>
            {reserveUpgradeSlot && (
              <span className={clsxm(PILL, GHOST_PILL, 'gap-1 px-3')}>
                <span className='h-3.5 w-3.5' />
                {labels.upgrade}
              </span>
            )}
          </span>
        </div>
        <div className='col-start-1 row-start-1 flex items-center gap-0.5 justify-self-end'>
          {deletable && (
            <IconButton
              label={labels.remove}
              onClick={onDelete}
              className={REVEAL}
            >
              <TrashIcon className='h-4 w-4' />
            </IconButton>
          )}
          <IconButton
            label={favorite ? labels.unfavorite : labels.favorite}
            pressed={favorite}
            onClick={onToggleFavorite}
            className={
              favorite
                ? 'text-rose-500 hover:text-rose-600 dark:text-rose-400'
                : REVEAL
            }
          >
            {favorite ? (
              <HeartIcon className='h-4 w-4' />
            ) : (
              <HeartOutlineIcon className='h-4 w-4' />
            )}
          </IconButton>
          {locked ? (
            <Link
              href='/settings/billing'
              onClick={(event) => event.stopPropagation()}
              className={clsxm(
                PILL,
                'ml-1 inline-flex gap-1 bg-amber-50 px-3 text-amber-700 hover:bg-amber-100 dark:bg-amber-500/15 dark:text-amber-300',
              )}
            >
              <LockClosedIcon className='h-3.5 w-3.5' />
              {labels.upgrade}
            </Link>
          ) : selected ? (
            // Phones show only the check, in a circle; the label stays for
            // screen readers.
            <span
              className={clsxm(
                PILL,
                'ml-1 inline-flex w-8 justify-center bg-primary-600 text-white sm:w-auto sm:justify-start sm:gap-1 sm:pl-2 sm:pr-3',
              )}
            >
              <CheckIcon className='h-4 w-4' />
              <span className='sr-only sm:not-sr-only'>{labels.selected}</span>
            </span>
          ) : (
            <button
              type='button'
              onClick={(event) => {
                event.stopPropagation();
                onUse();
              }}
              className={clsxm(
                PILL,
                'ml-1 hidden bg-gray-900 px-3.5 text-white hover:bg-gray-800 sm:inline-flex',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:bg-white dark:text-gray-900 dark:hover:bg-slate-200',
                REVEAL,
              )}
            >
              {labels.use}
            </button>
          )}
        </div>
      </div>
    </li>
  );
};

export default VoiceRow;
